import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  History as HistoryIcon,
  TrendingUp as PromoteIcon,
  Restore as RollbackIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as ActiveIcon,
  Warning as DeprecatedIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getVersionNumber, formatDate } from '../utils/versionUtils';
import VersionStatusBadge from './VersionStatusBadge';

/**
 * VersionList Component
 * Displays the list of template versions with actions
 */
const VersionList = ({
  versions,
  selectedVersions,
  templateId,
  template,
  policySummaryByVersion,
  onVersionSelect,
  onPromoteVersion,
  onRollbackVersion,
  onChangeStatus, // New prop for status change
}) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedVersionForMenu, setSelectedVersionForMenu] = useState(null);

  const handleMenuOpen = (event, version) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedVersionForMenu(version);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedVersionForMenu(null);
  };

  const handleStatusChange = () => {
    if (selectedVersionForMenu && onChangeStatus) {
      onChangeStatus(selectedVersionForMenu);
    }
    handleMenuClose();
  };

  const getStatusActions = version => {
    const status = version.status?.toLowerCase() || 'draft';
    const actions = [];

    switch (status) {
      case 'draft':
        actions.push(
          {
            label: 'Promote to Active',
            status: 'active',
            icon: <ActiveIcon />,
          },
          {
            label: 'Mark as Deprecated',
            status: 'deprecated',
            icon: <DeprecatedIcon />,
          },
          { label: 'Archive', status: 'archived', icon: <ArchiveIcon /> }
        );
        break;
      case 'active':
        actions.push(
          {
            label: 'Deactivate (Deprecated)',
            status: 'deprecated',
            icon: <DeprecatedIcon />,
          },
          { label: 'Archive', status: 'archived', icon: <ArchiveIcon /> }
        );
        break;
      case 'deprecated':
        actions.push(
          { label: 'Reactivate', status: 'active', icon: <ActiveIcon /> },
          { label: 'Archive', status: 'archived', icon: <ArchiveIcon /> }
        );
        break;
      case 'archived':
        // No actions for archived versions
        break;
      default:
        break;
    }

    return actions;
  };

  const getModelInfo = version => {
    const rawConfig = version?.model_config;
    if (!rawConfig) {
      return { provider: 'unknown', model: 'unknown' };
    }

    let parsed = rawConfig;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { provider: 'unknown', model: 'unknown' };
      }
    }

    return {
      provider: parsed.provider || 'unknown',
      model: parsed.model_name || 'unknown',
    };
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <HistoryIcon sx={{ mr: 1 }} />
        Version History ({versions.length} versions)
      </Typography>

      {versions.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No versions found for this template.
          </Typography>
        </Box>
      ) : (
        <List>
          {versions.map((version, index) => {
            const versionNumber =
              version.version_number ||
              getVersionNumber(version.version || version.id);
            const isSelected = selectedVersions.includes(
              version.version || versionNumber
            );
            const statusActions = getStatusActions(version);
            const { provider, model } = getModelInfo(version);
            const format = version.output_format || 'text';
            const category = (template && template.category) || 'general';
            const versionKey =
              version.version_number ||
              getVersionNumber(version.version || version.id);
            const policyInfo = (policySummaryByVersion &&
              policySummaryByVersion[versionKey]) || {
              summary: 'N/A',
              isActive: false,
            };
            const isMenuOpen =
              menuAnchor !== null && selectedVersionForMenu?.id === version.id;

            return (
              <ListItem
                key={version.id || version.version}
                sx={{
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: isSelected
                    ? 'primary.50'
                    : 'background.paper',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  onVersionSelect(version.version || versionNumber)
                }
              >
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Version {versionNumber}
                      </Typography>
                      <VersionStatusBadge
                        status={version.status || 'draft'}
                        clickable={!!onChangeStatus}
                        onClick={e => {
                          e.stopPropagation();
                          if (onChangeStatus) {
                            onChangeStatus(version);
                          }
                        }}
                      />
                      {version.status === 'active' && (
                        <Chip
                          label="Current"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        {version.description || 'No description provided'}
                      </Typography>
                      <Box mt={0.75}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'primary.main' }}
                        >
                          Provider:{' '}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                          >
                            {provider}
                          </Box>
                          {' | '}Model:{' '}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                          >
                            {model}
                          </Box>
                          {' | '}Format:{' '}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                          >
                            {format}
                          </Box>
                          {' | '}Category:{' '}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                          >
                            {category}
                          </Box>
                        </Typography>
                      </Box>
                      <Box mt={0.25} display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'primary.main' }}
                        >
                          Security Policy:{' '}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                          >
                            {policyInfo.summary}
                          </Box>
                        </Typography>
                        {policyInfo.summary !== 'N/A' && (
                          <Chip
                            label={policyInfo.isActive ? 'Active' : 'Disabled'}
                            size="small"
                            color={policyInfo.isActive ? 'success' : 'default'}
                            variant={
                              policyInfo.isActive ? 'filled' : 'outlined'
                            }
                          />
                        )}
                      </Box>
                      <Box mt={0.5}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          Created: {formatDate(version.created_at)} | Updated:{' '}
                          {formatDate(version.updated_at)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.25 }}
                        >
                          Created by: {version.created_by || 'N/A'} | Updated
                          by:{' '}
                          {version.updated_by || version.created_by || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" gap={1}>
                    {version.status !== 'active' && onPromoteVersion && (
                      <Tooltip title="Promote to Active">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={e => {
                            e.stopPropagation();
                            onPromoteVersion(version);
                          }}
                        >
                          <PromoteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onChangeStatus && statusActions.length > 0 && (
                      <>
                        <Tooltip title="Change Status">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={e => handleMenuOpen(e, version)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={menuAnchor}
                          open={isMenuOpen}
                          onClose={handleMenuClose}
                          onClick={e => e.stopPropagation()}
                        >
                          {statusActions.map((action, idx) => (
                            <MenuItem key={idx} onClick={handleStatusChange}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {action.icon}
                                {action.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                    <Tooltip title="Rollback to this version">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={e => {
                          e.stopPropagation();
                          onRollbackVersion(version);
                        }}
                      >
                        <RollbackIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit this version">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(
                            `/templates/${templateId}/versions/${version.id}/edit`,
                            {
                              state: {
                                from: `/templates/${templateId}/versions`,
                              },
                            }
                          );
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default VersionList;
